import { formatSpecLabel, formatSpecValue, OTHER_CATEGORY_NAME } from '../../utils/specFormatting';

/**
 * Generic, conservative heuristics for flagging bad scrape data in the
 * Specifications tab and duplicated copy in the Details tab. These operate
 * on real product data (any brand's scrape), not just the one demo product
 * the design mockup hardcoded flags for — so each check is deliberately
 * narrow and false-negative-biased: skip flagging when unsure rather than
 * risk drowning the "N to check" filter in noise.
 */

// Categories where a spec value is expected to carry a physical unit, so a
// bare number is worth a second look (e.g. "24.5" instead of "24.5 MP").
const MEASUREMENT_CATEGORIES = new Set([
  'Image Sensor',
  'Display & Viewfinder',
  'Physical & Design',
  'Power & Battery',
]);

const BARE_NUMBER_RE = /^\d+(\.\d+)?$/;

function isBareNumber(value: string): boolean {
  return BARE_NUMBER_RE.test(value.trim());
}

/**
 * The kinds of problem `computeSpecIssues` can report, with the phrase each
 * one contributes to the "N to check" summary, so that summary names only the
 * kinds that actually fired.
 */
export type SpecIssueKind = 'swapped-label' | 'missing-unit' | 'unmapped-key' | 'cross-field';

export const SPEC_ISSUE_PHRASES: Record<SpecIssueKind, string> = {
  'swapped-label': 'a swapped label and value',
  'missing-unit': 'values with no units',
  'unmapped-key': 'unmapped scrape keys',
  'cross-field': 'cross-field disagreements',
};

const MISSING_UNIT_MESSAGE = 'No unit on this value — likely missing a unit suffix.';
const UNMAPPED_KEY_MESSAGE = 'Unmapped scrape key with a bare numeric value — likely belongs to a real field.';

/** Classify a message produced by `computeSpecIssues`. */
export function specIssueKind(message: string): SpecIssueKind {
  if (message === MISSING_UNIT_MESSAGE) return 'missing-unit';
  if (message === UNMAPPED_KEY_MESSAGE) return 'unmapped-key';
  if (message.startsWith('Value reads like a field name')) return 'swapped-label';
  return 'cross-field';
}

/**
 * Per-spec flag reasons, keyed by spec key. `categorized` is the same
 * category grouping the tab renders (see `categorizeSpecKeys`), so a spec
 * landing in "Other" here is exactly what the tab shows as unmapped.
 */
export function computeSpecIssues(
  categorized: Map<string, string[]>,
  getValue: (key: string) => string | null,
  dimensions?: { weight?: number; weightUnit?: string }
): Map<string, string> {
  const flags = new Map<string, string>();

  // Every spec's own formatted label, for swapped-label/value detection —
  // built across all categories since a swap can point at any other field.
  const allLabels = new Set<string>();
  for (const keys of Array.from(categorized.values())) {
    for (const key of keys) {
      allLabels.add(formatSpecLabel(key).trim().toLowerCase());
    }
  }

  for (const [category, keys] of Array.from(categorized.entries())) {
    for (const key of keys) {
      const rawValue = getValue(key);
      if (!rawValue) continue;
      const value = formatSpecValue(key, rawValue).trim();
      if (!value) continue;

      // Swapped label/value: the value reads as some spec's field name.
      if (allLabels.has(value.toLowerCase())) {
        flags.set(key, `Value reads like a field name ("${value}") — label and value may be swapped.`);
        continue;
      }

      // No unit: a bare number in a category where values are normally
      // dimensioned (mm, MP, cd/m², oz, ...).
      if (MEASUREMENT_CATEGORIES.has(category) && isBareNumber(value)) {
        flags.set(key, MISSING_UNIT_MESSAGE);
        continue;
      }

      // Unmapped scrape key ("Other") that also carries a junk signal — a
      // bare number with no field name to give it meaning. Plain "Other"
      // membership alone is too broad (human-readable Sony-style keys often
      // land there) to flag on its own.
      if (category === OTHER_CATEGORY_NAME && isBareNumber(value)) {
        flags.set(key, UNMAPPED_KEY_MESSAGE);
        continue;
      }
    }
  }

  // Cross-field check: a "weight"-named spec vs. the product's own
  // dimensions.weight, unit-converted rather than string-compared.
  if (dimensions?.weight != null) {
    for (const keys of Array.from(categorized.values())) {
      for (const key of keys) {
        if (flags.has(key) || !/weight/i.test(key)) continue;
        const rawValue = getValue(key);
        if (!rawValue) continue;
        const disagreement = weightDisagreement(rawValue, dimensions.weight, dimensions.weightUnit);
        if (disagreement) flags.set(key, disagreement);
      }
    }
  }

  return flags;
}

const WEIGHT_UNIT_TO_GRAMS: Record<string, number> = {
  g: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilogram: 1000,
  kilograms: 1000,
  oz: 28.3495,
  ounce: 28.3495,
  ounces: 28.3495,
  lb: 453.592,
  lbs: 453.592,
  pound: 453.592,
  pounds: 453.592,
};

function parseWeightGrams(text: string): number | null {
  const m = String(text).match(/(\d+(?:\.\d+)?)\s*(kilograms?|kg|grams?|g|ounces?|oz|pounds?|lbs?)\b/i);
  if (!m) return null;
  const num = parseFloat(m[1]);
  const mult = WEIGHT_UNIT_TO_GRAMS[m[2].toLowerCase()];
  if (!Number.isFinite(num) || mult == null) return null;
  return num * mult;
}

/**
 * Compares a free-text spec weight (e.g. "1.19 lbs (without battery and
 * cards)") against the product's own dimensions.weight + weightUnit,
 * unit-converted to grams with a small tolerance for rounding. Returns a
 * human-readable disagreement note, or null when they reconcile or either
 * side can't be parsed.
 */
export function weightDisagreement(
  specWeightText: string,
  dimensionsWeight: number,
  dimensionsWeightUnit?: string
): string | null {
  const specGrams = parseWeightGrams(specWeightText);
  if (specGrams == null) return null;
  const dimGrams = parseWeightGrams(`${dimensionsWeight} ${dimensionsWeightUnit || 'g'}`);
  if (dimGrams == null) return null;
  const diffRatio = Math.abs(specGrams - dimGrams) / Math.max(specGrams, dimGrams);
  if (diffRatio <= 0.03) return null;
  return `Overview lists ${dimensionsWeight}${dimensionsWeightUnit || 'g'} — the two disagree.`;
}

// ---------------------------------------------------------------------------
// Feature-restates-a-spec heuristic (Details tab)
// ---------------------------------------------------------------------------

const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'with', 'for', 'of', 'in', 'on', 'to', 'is',
  'are', 'as', 'at', 'by', 'from', 'this', 'that', 'your', 'you', 'its',
]);

function significantWords(text: string): Set<string> {
  const words = String(text).toLowerCase().match(/[a-z0-9.]+/g) ?? [];
  return new Set(words.filter((w) => w.length > 2 && !STOPWORDS.has(w)));
}

function sharedNumbers(a: string, b: string): string[] {
  const numsA = new Set(String(a).match(/\d+(?:\.\d+)?/g) ?? []);
  const numsB = String(b).match(/\d+(?:\.\d+)?/g) ?? [];
  return numsB.filter((n) => numsA.has(n));
}

/**
 * True when a feature bullet appears to just restate a spec value: it must
 * share a distinctive number (a spec count, a stop value, a size) AND
 * several non-trivial words with that spec — a shared number alone (e.g. a
 * stray model number) isn't enough to flag on its own, and neither is a
 * generic word overlap without a shared number to anchor it.
 */
export function featureRestatesSpec(featureText: string, specValues: string[]): boolean {
  const featureWords = significantWords(featureText);
  for (const specValue of specValues) {
    if (sharedNumbers(featureText, specValue).length === 0) continue;
    const specWords = significantWords(specValue);
    let overlap = 0;
    for (const w of Array.from(specWords)) {
      if (featureWords.has(w)) overlap += 1;
    }
    if (overlap >= 2) return true;
  }
  return false;
}
